import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { resolvePreflightWarningBody, type PreflightWarning } from '@signalvote/shared';

interface PortalActionListProps {
  /** The full preflight warning list; only the portal-action ones are shown. */
  warnings: readonly PreflightWarning[];
  /** Interactive row for a caller that takes dispositions. Defaults to a read-only line. */
  renderItem?: (warning: PreflightWarning) => ReactNode;
}

/**
 * The "Portal actions required after you upload" group: the warnings whose remedy is a
 * step in the state portal the file cannot carry. ADR-025: membership is the per-warning
 * `portalActionRequired` wire flag, never a list of warning types.
 */
export function PortalActionList({ warnings, renderItem = readOnlyItem }: PortalActionListProps) {
  const portalActions = warnings.filter((w) => w.portalActionRequired ?? false);
  if (portalActions.length === 0) return null;

  return (
    <div
      className="space-y-2 rounded-md border-2 border-amber-500 bg-amber-100 p-2 text-sm"
      data-testid="heads-up-portal-actions-group"
    >
      <div
        className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-amber-900"
        data-testid="heads-up-portal-actions-label"
      >
        <AlertTriangle className="h-3.5 w-3.5" />
        Portal actions required after you upload
      </div>
      <ul className="space-y-2">{portalActions.map(renderItem)}</ul>
    </div>
  );
}

function readOnlyItem(warning: PreflightWarning) {
  return (
    <li key={warning.warningId} className="text-amber-900">
      {resolvePreflightWarningBody(warning.warningType, warning.affectedRowIds.length)
        ?? warning.warningText}
    </li>
  );
}
